import React, { useEffect, useState } from "react"
import { ErrorBoundary } from "../ErrorBoundary"
import { AppCard } from "./AppCard"
import { AddAppForm } from "./AddAppForm"
import { EmptyState } from "./EmptyState"

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content

// App Setup (doc 04). Fetches discovered apps from the index JSON, renders a
// responsive card grid, and wires rename/confirm (PATCH) + resync (POST). The
// partner_dashboard layout supplies the surrounding chrome.
export default function AppSetupApp({ appsPath, settingsPath, copy }) {
  const [apps, setApps] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    let active = true
    fetch(appsPath, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((data) => active && setApps(data))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [appsPath])

  const basePath = appsPath.replace(/\.json.*$/, "")

  const updateApp = (id, attrs) =>
    fetch(`${basePath}/${id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
        Accept: "application/json",
      },
      body: JSON.stringify({ partner_app: attrs }),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((updated) =>
        setApps((prev) => prev.map((a) => (a.id === updated.id ? updated : a)))
      )

  const resyncApp = (id) =>
    fetch(`${basePath}/${id}/resync`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
    }).then((res) => (res.ok ? res.json() : Promise.reject(res)))

  const removeApp = (id) =>
    fetch(`${basePath}/${id}`, {
      method: "DELETE",
      headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
    }).then((res) => {
      if (!res.ok) return Promise.reject(res)
      setApps((prev) => (prev || []).filter((a) => a.id !== id))
    })

  const createApp = (name) =>
    fetch(basePath, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
        Accept: "application/json",
      },
      body: JSON.stringify({ partner_app: { name } }),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((created) => setApps((prev) => [created, ...(prev || [])]))

  const header = (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{copy.title}</h1>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{copy.subtitle}</p>
    </div>
  )

  const shell = (body) => (
    <ErrorBoundary>
      {header}
      <div className="mt-8">{body}</div>
    </ErrorBoundary>
  )

  if (loading) return shell(<p className="text-sm text-gray-400 dark:text-gray-500">{copy.states.loading}</p>)
  if (error || !apps) {
    return shell(
      <div className="rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/40 p-6">
        <p className="text-sm text-gray-700 dark:text-gray-300">{copy.states.error}</p>
      </div>
    )
  }
  if (apps.length === 0) {
    return shell(
      <div className="space-y-6">
        <EmptyState copy={copy} settingsPath={settingsPath} />
        <AddAppForm copy={copy} onCreate={createApp} />
      </div>
    )
  }

  return shell(
    <div className="space-y-6">
      <div className="flex justify-end">
        <AddAppForm copy={copy} onCreate={createApp} />
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {apps.map((app) => (
          <AppCard
            key={app.id}
            app={app}
            copy={copy}
            detailPath={`${basePath}/${app.id}`}
            onUpdate={updateApp}
            onResync={resyncApp}
            onRemove={removeApp}
          />
        ))}
      </div>
    </div>
  )
}
